// Wraps InfiniteScroll to provide a more convenient interface for fetching more entries.
//
// Usage:
//   import { useMyInfiniteScroll } from '../../components/MyInfiniteScroll';
//   ... Then in your component:
//   const renderChildren= (entries) => {
//    return entries.map((entry, index) => (
//        <LogbookEntry key={index} entry={entry} onDetails={onDetails} />
//      ));
//    }
//  
//    const fetchMoreEntries = useCallback(async (pageToken) => {
//       // Fetch logbook entries for the current page. Note that nextToken could be null, so use expansion
//       // operator to optionally add it.
//       const query = { 
//         query: completedTasksByItemId, 
//         variables: { 
//           itemId: itemId, 
//           sortDirection: 'DESC', 
//           ...(pageToken && { nextToken: pageToken} )
//         }
//       };
//       let results = await client.graphql(query);  
//       return  { 
//         nextToken: results.data.completedTasksByItemId.nextToken, 
//         items: results.data.completedTasksByItemId.items 
//       };
//    }, [itemId]);
//
//    const { InfiniteScroll, entries, setEntries } = useMyInfiniteScroll(renderChildren, fetchEntries);
// 
//    return (
//      <div>
//        <InfiniteScroll />
//      </div>

import InfiniteScroll from "./infinite-scroll/index.tsx";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useIsMounted } from "../utilities/useMounted";
import { LoadingMessage, useStatusMessage } from "./StatusMessage";

// Union two entry lists by `id`, keeping `existing` order and dropping any
// incoming row already present.
//
// EVERY setEntries here goes through this, in the UPDATER form, and that is
// load-bearing. A page fetch computes its result before an await, so writing a
// plain value silently discards anything that landed during the await --
// including a consumer's optimistic insert (Items.saveItem appends the row it
// just created). The list never refetches after a create, and a query that
// started before the row existed can never return it, so a clobbered insert is
// gone until reload: create an item while a page fetch is in flight and it
// vanishes from the list. Reading `prev` at apply time is what fixes it.
//
// Deduping is not a bonus, it is required by the above: once an optimistic row
// is preserved, a later page containing that same row would render it twice.
// (That double-render already existed -- the e2e specs used to locate list
// rows with `.first()` to tolerate it -- it just needed a scroll far enough to
// refetch. Those locators are strict now; the guard is the unit tests in
// __tests__/MyInfiniteScroll.test.jsx.)
//
// Returns `existing` by reference when nothing is new, so React can skip the
// render.
export function mergeEntriesById(existing, incoming) {
    const base = existing ?? [];
    const list = incoming ?? [];
    if (list.length === 0) {
        return base;
    }

    const seen = new Set(base.map((entry) => entry?.id).filter(Boolean));
    const added = list.filter((entry) => {
        // A row with no id can't be identified, so it can't be deduped either;
        // keeping it matches the old concatenating behaviour.
        if (!entry?.id) return true;
        if (seen.has(entry.id)) return false;
        seen.add(entry.id);
        return true;
    });

    return added.length === 0 ? base : [...base, ...added];
}

// UI component returned by useMyInfiniteScroll.  You can use this in your component.
const MyInfiniteScroll = ({ 
    entries, 
    setEntries, 
    pageToken, 
    setPageToken, 
    hasMoreEntries, 
    setHasMoreEntries, 
    endMessage,
    fetchEntries, 
    renderChildren 
}) => {
    // See if we're still mounted after async calls.
    const isMounted = useIsMounted();
    const [StatusMessage, { showError, hideStatus }] = useStatusMessage();

    const fetchMoreEntries = useCallback(async () => {
        try {
            if (!isMounted()) {
                console.log("MyInfiniteScroll fetchMoreEntries: Not mounted.");
                return;
            }

            let { nextToken, items } = await fetchEntries(pageToken);
            console.log(`MyInfiniteScroll fetched ${items.length} more entries.`)

            if (isMounted()) {
                // Remember any next token.
                setPageToken(nextToken);
                setHasMoreEntries(!!nextToken);

                // Append onto whatever the list holds NOW, not onto the copy
                // this closure captured before the await -- see
                // mergeEntriesById.
                setEntries((prev) => mergeEntriesById(prev, items));
                hideStatus();
            }
        } catch (err) {
            showError(err);
        }
    }, [fetchEntries, isMounted, pageToken, setEntries, setHasMoreEntries, setPageToken, showError, hideStatus]);

    return (
        <span>
            <InfiniteScroll
                dataLength={entries ? entries.length : 0}
                next={fetchMoreEntries}
                hasMore={hasMoreEntries}
                loader={<LoadingMessage />}
                endMessage={endMessage}
            >
                {renderChildren(entries)}
            </InfiniteScroll>
            <StatusMessage />
        </span>
    );
}

// This is the wrapped InfiniteScroll with a StatusMessage that you can use in your component.
const InfiniteScrollWithMessage = ({entries, setEntries, fetchEntries, renderChildren, pageToken, setPageToken, hasMoreEntries, setHasMoreEntries, endMessage, Message}) => {
    return (
        <span>
            <MyInfiniteScroll
                entries={entries}
                setEntries={setEntries}
                fetchEntries={fetchEntries}
                renderChildren={renderChildren}
                pageToken={pageToken}
                setPageToken={setPageToken}
                hasMoreEntries={hasMoreEntries}
                setHasMoreEntries={setHasMoreEntries}
                endMessage={endMessage && endMessage()}
            />
            {Message}
        </span>
    );
}


// Infinite scroll component that fetches more entries as needed. You supply
// the fetch function to call to get more entries in the form (nextToken || null) => { nextToken, items }.
// Returns an InfiniteScroll component that you can use in your component, the entries array and a setEntries
// function. The entries/setEntries are only needed if you want to manage the entries yourself.
// Entries is null until the first page of entries is fetched.
export const useMyInfiniteScroll = (renderChildren, fetchEntries, entries, setEntries, endMessage) => {
    // Array of entries showing.
    const [StatusMessage, {showError, hideStatus}] = useStatusMessage();

    // For infinite scroll, the next token to fetch more entries and whether there are more entries.
    const [pageToken, setPageToken] = useState(null);
    const [hasMoreEntries, setHasMoreEntries] = useState(true);
    
    // See if we're still mounted after async calls.
    const isMounted = useIsMounted();

    // Catch if caller has a bug where fetchEntries function changes. Should be a constant (created with useCallback).
    const alreadyFetched = useRef(false);

    // Fetch the first page of entries on page load. The InfiniteScroll component doesn't do an initial
    // fetch, so I do it.
    useEffect(() => {
        async function fetchData() {
            try {
                console.log("useMyInfiniteScroll fetchData().");

                // Show loading UI
                setEntries(null);
                setHasMoreEntries(true);

                // Go get the first page of entries.
                let { nextToken, items } = await fetchEntries(null);
                console.log(`useMyInfiniteScroll fetched ${items.length} entries.`)
                if (isMounted()) {
                    // When a query uses filters, the server can return a nextToken with no items.
                    // The InfiniteScroll component doesn't then re-fetch. So, I keep fetching here
                    // until I get items.
                    //
                    // Cap the retry count so a query that paginates through
                    // many empty pages (e.g. a sort-key-less filter against
                    // an item with thousands of non-matching rows) can't
                    // hang the page on a never-resolving spinner. When we
                    // hit the cap we surface what we have (typically zero
                    // entries) along with the nextToken so the InfiniteScroll
                    // can keep fetching on user scroll instead of in a
                    // tight loop here.
                    const MAX_INITIAL_REFETCHES = 25;
                    let retries = 1;
                    while (items.length === 0 && nextToken && retries <= MAX_INITIAL_REFETCHES) {
                        console.log(`useMyInfiniteScroll initial fetch got no items, and nextToken. Refetching ${retries}.`);
                        let result = await fetchEntries(nextToken);
                        if (!isMounted()) {
                            return;
                        }
                        items = result.items;
                        nextToken = result.nextToken;
                        retries = retries + 1;
                    }
                    if (items.length === 0 && nextToken) {
                        console.warn(
                            `useMyInfiniteScroll initial fetch hit ${MAX_INITIAL_REFETCHES} ` +
                            `empty refetches; surrendering and letting InfiniteScroll drive ` +
                            `further pagination on scroll.`
                        );
                    }

                    // Remember any next token.
                    setPageToken(nextToken);
                    setHasMoreEntries(nextToken !== null);

                    // The first page REPLACES the list -- but anything the
                    // consumer inserted while this was loading has to survive,
                    // or a row created during a slow first load disappears.
                    // Merging is safe precisely because the list was set to
                    // null above: whatever `prev` holds now arrived during
                    // this fetch, so there are no stale rows to resurrect. The
                    // server's copy wins for any id in both.
                    setEntries((prev) => mergeEntriesById(items, prev));
                    hideStatus();
                } else {
                    console.log("useMyInfiniteScroll fetchData(): Not mounted.");
                }
            } catch (err) {
                // Show the error message.
                showError(err);

                // Clear the entries so the InfiniteScroll doesn't try to render them.
                setEntries(null);
                setHasMoreEntries(false);
                setPageToken(null);
            }
        }

        // Catch a bug where fetchEntries is not a constant created with useCallback, resulting in multiple calls (and infinite loop?).
        if (alreadyFetched.current) {
            console.error("useMyInfiniteScroll: Initial useEffect called twice! Probably fetchEntries is not a constant created with useCallback.");

            // TODO: While developing, sometimes the page reloads when I change code. I do want to reload, and do it from the beginning.
            // return;
        }

        if (!isMounted) {
            console.log("useMyInfiniteScroll fetchData: Not mounted.");
            return;
        }
        alreadyFetched.current = true;
        fetchData();
    }, [fetchEntries, hideStatus, isMounted, setEntries, showError]);

    const handleRenderChildren = useCallback((items) => {
        // If the first fetch hasn't completed, nothing to render.
        if (null === items) {
            return null;
        }

        return renderChildren(items);
    }, [renderChildren]);    

    // Everything the control renders with, refreshed on every render and read
    // through a ref so `Control` itself can keep ONE identity for the life of
    // the hook.
    //
    // That identity matters more than it looks. Consumers render the control as
    // `<Control />`, so a new function identity is a new element TYPE, and React
    // answers that by unmounting the whole list and mounting it again. Rebuilding
    // it on every `entries` change meant every row's DOM node was destroyed and
    // recreated whenever anything was added -- and, far worse, it unmounted the
    // component holding any in-flight page fetch, so that page's own isMounted()
    // guard then threw the result away. An optimistic insert during a fetch
    // silently discarded the fetch. Reading from a ref keeps the type stable;
    // the control still re-renders with fresh values because the consumer
    // re-renders whenever `entries` changes, and it owns that state.
    const latest = useRef(null);
    latest.current = {
        entries,
        setEntries,
        fetchEntries,
        renderChildren: handleRenderChildren,
        pageToken,
        setPageToken,
        hasMoreEntries,
        setHasMoreEntries,
        endMessage,
        StatusMessage,
    };

    const Control = useMemo(
        () => function InfiniteScrollControl() {
            const { StatusMessage: Status, ...rest } = latest.current;
            return <InfiniteScrollWithMessage {...rest} Message={<Status />} />;
        },
        []
    );

    // TODO: Remove entries/setEntries, they are passed in to useMyInfiniteScroll now...

    return useMemo(() => { return { 
        Control,
        entries, 
        setEntries 
    }}, [Control, entries, setEntries]);
}
